import React, { useMemo, useState } from 'react';
import { Modal, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import useSWR from 'swr';
import { listAssets, type Asset } from '../../data/Asset';
import AssetDropzone from './studio/AssetDropzone';
import AssetTile from './studio/AssetTile';
import { matchesStudioFilter } from './studio/studioUtils';

type AssetPickerProps = {
    opened: boolean;
    onClose: () => void;
    onSelect: (asset: Asset) => void;
    /** Highlighted in the grid so re-opening the picker shows the current choice. */
    selectedAssetId?: string | null;
};

/**
 * Choose an existing image or upload a new one, without leaving the component
 * form. A freshly uploaded image is selected immediately — uploading here is
 * always in service of picking something, so making the user then hunt for it in
 * the grid would be busywork.
 */
const AssetPicker: React.FC<AssetPickerProps> = ({
    opened,
    onClose,
    onSelect,
    selectedAssetId,
}) => {
    const [query, setQuery] = useState('');
    const { data: assets = [], isLoading } = useSWR(opened ? 'api/assets' : null, listAssets);

    const filtered = useMemo(
        () => assets.filter(asset => matchesStudioFilter(asset.name, query)),
        [assets, query],
    );

    const handleSelect = (asset: Asset) => {
        onSelect(asset);
        onClose();
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Choose an image" size="lg" centered>
            <Stack gap="md">
                <AssetDropzone compact onUploaded={handleSelect} />

                <TextInput
                    placeholder="Filter images…"
                    value={query}
                    onChange={e => setQuery(e.currentTarget.value)}
                    leftSection={<IconSearch size={16} stroke={1.5} />}
                />

                {isLoading ? (
                    <Text c="dimmed" size="sm">
                        Loading…
                    </Text>
                ) : filtered.length === 0 ? (
                    <Text c="dimmed" size="sm">
                        {assets.length === 0
                            ? 'No images yet. Drop one above to get started.'
                            : 'No images match that filter.'}
                    </Text>
                ) : (
                    <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
                        {filtered.map(asset => (
                            <AssetTile
                                key={asset.id}
                                asset={asset}
                                selected={asset.id === selectedAssetId}
                                onClick={() => handleSelect(asset)}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </Stack>
        </Modal>
    );
};

export default AssetPicker;
